/**
 * Finance business-line guards for honest Boost.
 * Prevent weaving franchise labels (IB / CB / AM / …) into a role whose
 * source evidence clearly lives in a different line.
 */

/** @typedef {'investment_banking'|'commercial_banking'|'asset_management'|'wealth_management'|'private_equity'|'equity_research'|'sales_trading'} BusinessLine */

/**
 * Franchise / desk labels that shift how a recruiter reads a role.
 * Cross-cutting domains (regulatory reporting, valuation, risk management)
 * are intentionally NOT listed here.
 */
const FRANCHISE_KEYWORD_TO_LINE = new Map(
  [
    ["investment banking", "investment_banking"],
    ["investment bank", "investment_banking"],
    ["mergers and acquisitions", "investment_banking"],
    ["m&a", "investment_banking"],
    ["commercial banking", "commercial_banking"],
    ["corporate banking", "commercial_banking"],
    ["consumer and small business banking", "commercial_banking"],
    ["retail banking", "commercial_banking"],
    ["consumer banking", "commercial_banking"],
    ["asset management", "asset_management"],
    ["asset allocation", "asset_management"],
    ["investment management", "asset_management"],
    ["portfolio management", "asset_management"],
    ["wealth management", "wealth_management"],
    ["private wealth", "wealth_management"],
    ["private equity", "private_equity"],
    ["equity research", "equity_research"],
    ["sales and trading", "sales_trading"],
    ["sales & trading", "sales_trading"],
  ].map(([k, v]) => [k.toLowerCase(), v]),
);

/** Evidence patterns that mark a business line as already present. */
const LINE_EVIDENCE_RE = {
  investment_banking:
    /\b(investment banking|investment bank|\bm&a\b|mergers and acquisitions|pitch book|ipo|ecm|dcm|leveraged finance)\b/i,
  commercial_banking:
    /\b(commercial banking|corporate banking|retail banking|consumer banking|consumer and small business banking|middle market lending|credit underwriting)\b/i,
  asset_management:
    /\b(asset management|asset allocation|investment management|portfolio management|portfolio manager|equity long|long\/short|aum)\b/i,
  wealth_management:
    /\b(wealth management|private wealth|high[- ]net[- ]worth|hnw|financial advisor)\b/i,
  private_equity:
    /\b(private equity|\bpe\b|lbo|buyout|co-investments?)\b/i,
  equity_research:
    /\b(equity research|sell[- ]side research|initiate coverage)\b/i,
  sales_trading:
    /\b(sales and trading|sales & trading|market making|prop trading)\b/i,
};

/**
 * @param {string} phrase
 * @returns {BusinessLine | null}
 */
export function franchiseLineForKeyword(phrase) {
  const key = String(phrase || "")
    .trim()
    .toLowerCase()
    .replace(/[''`]/g, "");
  if (!key) return null;
  if (FRANCHISE_KEYWORD_TO_LINE.has(key)) return FRANCHISE_KEYWORD_TO_LINE.get(key);
  // Loose contains for longer job labels.
  for (const [k, line] of FRANCHISE_KEYWORD_TO_LINE.entries()) {
    if (key.includes(k) || k.includes(key)) return line;
  }
  return null;
}

/**
 * @param {string} text
 * @returns {Set<BusinessLine>}
 */
export function detectBusinessLines(text) {
  const hay = String(text || "");
  /** @type {Set<BusinessLine>} */
  const found = new Set();
  for (const [line, re] of Object.entries(LINE_EVIDENCE_RE)) {
    if (re.test(hay)) found.add(/** @type {BusinessLine} */ (line));
  }
  return found;
}

/**
 * True when weaving this keyword would invent a franchise the evidence never showed.
 * Soft / cross-cutting phrases always pass.
 * @param {string} phrase
 * @param {string} evidenceText
 */
export function isFranchiseCompatible(phrase, evidenceText) {
  const line = franchiseLineForKeyword(phrase);
  if (!line) return true;
  const text = String(evidenceText || "").trim();
  // No evidence → do not invent franchise labels.
  if (!text) return false;
  return detectBusinessLines(text).has(line);
}

/**
 * Role/bullet context for weave: title + company + bullets.
 * @param {{ title?: string, company?: string, bullets?: string[] }} role
 * @param {string} [bulletText]
 */
export function roleContextText(role, bulletText = "") {
  const parts = [
    role?.title,
    role?.company,
    ...(Array.isArray(role?.bullets) ? role.bullets : []),
    bulletText,
  ];
  return parts.filter(Boolean).join("\n");
}

/**
 * Can this franchise keyword be woven into this role without shifting its line?
 * If the role already shows a conflicting franchise and not the keyword's line → no.
 * If the role shows no franchise yet, require the line on the broader resume evidence.
 * @param {string} phrase
 * @param {string} roleText
 * @param {string} [resumeEvidenceText]
 */
export function canWeaveFranchiseIntoRole(phrase, roleText, resumeEvidenceText = "") {
  const line = franchiseLineForKeyword(phrase);
  if (!line) return true;

  const roleLines = detectBusinessLines(roleText);
  if (roleLines.has(line)) return true;

  // Role already reads as a different franchise → never graft another desk label
  // onto it, even if a *different* Experience role evidenced that line
  // (M&A at BDO must not unlock "across investment banking" on a PineBridge
  // equity-research bullet).
  if (roleLines.size > 0 && !roleLines.has(line)) {
    return false;
  }

  // Neutral role text → only allow if the resume as a whole already evidences that line.
  return isFranchiseCompatible(phrase, resumeEvidenceText || roleText);
}

const LINE_DISPLAY = {
  investment_banking: "investment banking",
  commercial_banking: "commercial banking",
  asset_management: "asset management",
  wealth_management: "wealth management",
  private_equity: "private equity",
  equity_research: "equity research",
  sales_trading: "sales and trading",
};

/**
 * For summary claims, require a literal desk label in Experience — not a weak
 * proxy. M&A diligence at an accounting firm must not unlock
 * "experience in investment banking".
 */
const SUMMARY_LITERAL_RE = {
  investment_banking: /\b(investment banking|investment bank)\b/i,
  commercial_banking:
    /\b(commercial banking|corporate banking|retail banking|consumer banking)\b/i,
  asset_management:
    /\b(asset management|investment management|portfolio management|asset allocation)\b/i,
  wealth_management: /\b(wealth management|private wealth)\b/i,
  private_equity: /\b(private equity|co-investments?)\b/i,
  equity_research: /\b(equity research|sell[- ]side research)\b/i,
  sales_trading: /\b(sales and trading|sales & trading)\b/i,
};

/**
 * Experience-only evidence (not clubs/activities). Summary claims like
 * "internships in investment banking" must be backed by real roles, not
 * alumni recruiting panels that merely mention IB.
 * @param {Record<string, unknown>} profile
 */
export function experienceFranchiseEvidence(profile) {
  const parts = [];
  for (const role of profile?.experience || []) {
    parts.push(roleContextText(role));
  }
  return parts.filter(Boolean).join("\n");
}

/**
 * Prefer an honest desk label already shown in experience roles.
 * @param {string} expText
 */
function preferredExperienceLabel(expText) {
  const order = [
    "asset_management",
    "private_equity",
    "wealth_management",
    "equity_research",
    "investment_banking",
    "commercial_banking",
    "sales_trading",
  ];
  for (const line of order) {
    const re = SUMMARY_LITERAL_RE[line];
    if (re?.test(expText)) return LINE_DISPLAY[line] || "finance";
  }
  // Soft fallback from broader detectors (e.g. PE via "buyout" only).
  const broad = detectBusinessLines(expText);
  for (const line of order) {
    if (broad.has(/** @type {BusinessLine} */ (line))) {
      return LINE_DISPLAY[line] || "finance";
    }
  }
  return "finance";
}

const FRANCHISE_CLAIM_RES = /** @type {Array<[RegExp, BusinessLine, string]>} */ ([
  [/\binvestment banking\b/gi, "investment_banking", "investment banking"],
  [/\bcommercial banking\b/gi, "commercial_banking", "commercial banking"],
  [/\bcorporate banking\b/gi, "commercial_banking", "corporate banking"],
  [/\bsales and trading\b/gi, "sales_trading", "sales and trading"],
  [/\bsales & trading\b/gi, "sales_trading", "sales & trading"],
  [/\bprivate equity\b/gi, "private_equity", "private equity"],
  [/\bwealth management\b/gi, "wealth_management", "wealth management"],
  [/\bequity research\b/gi, "equity_research", "equity research"],
  [/\basset management\b/gi, "asset_management", "asset management"],
]);

/**
 * Rewrite summary franchise claims that experience roles do not support
 * (e.g. "internships in investment banking" when roles are AM/PE only).
 * @param {Record<string, unknown>} profile
 * @param {Record<string, unknown>} sourceProfile
 * @returns {{ profile_json: Record<string, unknown>, actions: string[] }}
 */
export function sanitizeSummaryFranchises(profile, sourceProfile) {
  const current = { ...profile };
  let summary = String(current.summary || "");
  if (!summary.trim()) {
    return { profile_json: current, actions: [] };
  }

  const expText = experienceFranchiseEvidence(sourceProfile || profile);
  const actions = [];
  const fallback = preferredExperienceLabel(expText);

  for (const [re, line, label] of FRANCHISE_CLAIM_RES) {
    if (!re.test(summary)) continue;
    re.lastIndex = 0;
    const literal = SUMMARY_LITERAL_RE[line];
    if (literal?.test(expText)) continue;
    const next = summary.replace(re, fallback);
    if (next !== summary) {
      summary = next;
      actions.push(`sanitized summary franchise: ${label} → ${fallback}`);
    }
  }

  // Collapse accidental doubles after replacement.
  summary = summary.replace(/\b(asset management|private equity|finance)(?:\s+and\s+\1)+\b/gi, "$1");
  summary = summary.replace(/\s{2,}/g, " ").trim();

  current.summary = summary;
  return { profile_json: current, actions };
}

/**
 * Strip desk-label grafts from Experience bullets when the *source* role did not
 * literally show that franchise (e.g. "across investment banking" on an AM /
 * equity-research internship).
 * @param {Record<string, unknown>} profile
 * @param {Record<string, unknown>} sourceProfile
 * @returns {{ profile_json: Record<string, unknown>, actions: string[] }}
 */
export function sanitizeExperienceFranchises(profile, sourceProfile) {
  const sourceRoles = Array.isArray(sourceProfile?.experience) ? sourceProfile.experience : [];
  const roles = Array.isArray(profile?.experience) ? profile.experience : [];
  if (!roles.length) {
    return { profile_json: profile, actions: [] };
  }

  const actions = [];
  const nextRoles = roles.map((role, index) => {
    const sourceRole = sourceRoles[index] || {};
    const sourceText = roleContextText(sourceRole);
    const bullets = Array.isArray(role?.bullets) ? role.bullets : [];
    let touched = false;
    const nextBullets = bullets.map((raw) => {
      let text = String(raw || "");
      if (!text.trim()) return text;
      const before = text;
      for (const [re, line, label] of FRANCHISE_CLAIM_RES) {
        if (!re.test(text)) continue;
        re.lastIndex = 0;
        const literal = SUMMARY_LITERAL_RE[line];
        if (literal?.test(sourceText)) continue;
        text = stripGraftedFranchisePhrase(text, label);
      }
      if (text !== before) {
        touched = true;
        actions.push(`sanitized experience franchise in ${role?.company || role?.title || "role"}`);
      }
      return text;
    });
    if (!touched) return role;
    return { ...role, bullets: nextBullets };
  });

  if (!actions.length) {
    return { profile_json: profile, actions: [] };
  }
  return {
    profile_json: { ...profile, experience: nextRoles },
    actions,
  };
}

/**
 * Remove a desk label and the preposition that glued it on ("across/in/…").
 * @param {string} text
 * @param {string} label
 */
function stripGraftedFranchisePhrase(text, label) {
  const esc = String(label || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (!esc) return text;
  let out = String(text || "");
  out = out.replace(new RegExp(`\\s+(?:across|in|within|for|via|through)\\s+${esc}\\b`, "gi"), "");
  out = out.replace(new RegExp(`\\s*(?:,|;|\\band\\b)\\s*${esc}\\b`, "gi"), "");
  out = out.replace(new RegExp(`\\b${esc}\\b`, "gi"), "");
  out = out.replace(/\s{2,}/g, " ");
  out = out.replace(/\s+([.,;:])/g, "$1");
  out = out.replace(/\(\s*\)/g, "");
  return out.trim();
}
